import { CButton } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilArrowLeft } from '@coreui/icons'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useDebounce from 'src/hooks/useDebounce'
import PlayerService from 'src/service/PlayerService'
import PlayerStatTable from './PlayerStatTable'
import ClubService from 'src/service/ClubService'
import PlayerStatTableIndoor from './PlayerStatTableIndoor'
import PlayerValue from './PlayerValue'
import PlayerRound from './PlayerRound'
import PlayerChart from '../Chart/PlayerChart'

const TABS = [
  { key: 'statistics', label: 'Statistics' },
  { key: 'values', label: 'Values' },
  { key: 'round_points', label: 'Round Points' },
  { key: 'charts', label: 'Charts' },
]

const PlayerStats = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('statistics')
  // Each of the Values/Round Points/Charts tabs is a self-contained page component with its
  // own fetch-on-mount effect. Only mounting a tab the first time it's opened (and keeping it
  // mounted afterwards) means the page loads with a single request instead of four at once,
  // while switching back to an already-visited tab doesn't refetch.
  const [visitedTabs, setVisitedTabs] = useState(() => new Set(['statistics']))

  const [loading, setLoading] = useState()
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState([])
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(50)
  const [gameDetails, setGameDetails] = useState({})
  const [users, setUsers] = useState({})
  const [playerList, setPlayerList] = useState([])

  const dActivePage = useDebounce(activePage, 500)
  const dColumnFilter = useDebounce(columnFilter, 500)
  const dItemsPerPage = useDebounce(itemsPerPage, 500)

  useEffect(() => {
    fetchData(dActivePage, dColumnFilter, columnSorter, dItemsPerPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dActivePage, dColumnFilter, columnSorter, dItemsPerPage])

  useEffect(() => {
    const fetchGameAndPlayerData = async () => {
      try {
        setLoading(true)

        const [clubDetailsResponse, playerResponse] = await Promise.all([
          ClubService.getClubDetails(),
          PlayerService.getAllPlayer(),
        ])

        if (clubDetailsResponse) {
          setGameDetails(clubDetailsResponse.game_details)
        }

        if (playerResponse?.status === 200) {
          setPlayerList(playerResponse.data)
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchGameAndPlayerData()
  }, [])

  const fetchData = (activePage, columnFilter, columnSorter, itemsPerPage) => {
    const offset = itemsPerPage * activePage - itemsPerPage
    let params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      params.append(key, columnFilter[key])
    })
    columnSorter &&
      columnSorter.column !== undefined &&
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)

    PlayerService.playerStats(offset, itemsPerPage, activePage, params).then((result) => {
      setUsers({ ...result.data, stats: result.stats })
      setLoading(false)
    })
  }

  var columns = [
    {
      label: 'Player Name',
      filter: true,
      sorter: true,
      key: 'player_name',
      _props: { color: 'dark' },
    },
    { label: 'FP', filter: false, sorter: true, key: 'sum_fp', _props: { color: 'dark' } },
    { label: 'POS', filter: false, sorter: true, key: 'position_name', _props: { color: 'dark' } },
    { label: 'RS', filter: false, sorter: true, key: 'sum_rs', _props: { color: 'dark' } },
    { label: '4S', filter: false, sorter: true, key: 'sum_fours', _props: { color: 'dark' } },
    { label: '6S', filter: false, sorter: true, key: 'sum_sixes', _props: { color: 'dark' } },
    { label: 'OVR', filter: false, sorter: true, key: 'sum_overs', _props: { color: 'dark' } },
    { label: 'MDN', filter: false, sorter: true, key: 'sum_mdns', _props: { color: 'dark' } },
    { label: 'WKS', filter: false, sorter: true, key: 'sum_wks', _props: { color: 'dark' } },
    { label: 'CS', filter: false, sorter: true, key: 'sum_cs', _props: { color: 'dark' } },
    { label: 'CWK', filter: false, sorter: true, key: 'sum_cwks', _props: { color: 'dark' } },
    { label: 'STS', filter: false, sorter: true, key: 'sum_sts', _props: { color: 'dark' } },
    { label: 'ROD', filter: false, sorter: true, key: 'sum_rods', _props: { color: 'dark' } },
    { label: 'ROA', filter: false, sorter: true, key: 'sum_roas', _props: { color: 'dark' } },
    { label: 'DKS', filter: false, sorter: true, key: 'sum_dks', _props: { color: 'dark' } },
    { label: 'HT', filter: false, sorter: true, key: 'sum_hattrick', _props: { color: 'dark' } },
  ]
  if (gameDetails?.game_type === 3) {
    columns = [
      {
        label: 'Player Name',
        filter: true,
        sorter: true,
        key: 'player_name',
        _props: { color: 'dark' },
      },
      { label: 'FP', filter: false, sorter: true, key: 'sum_fp', _props: { color: 'dark' } },
      {
        label: 'POS',
        filter: false,
        sorter: true,
        key: 'position_name',
        _props: { color: 'dark' },
      },
      { label: 'RS', filter: false, sorter: true, key: 'sum_rs', _props: { color: 'dark' } },
      { label: '1S', filter: false, sorter: true, key: 'sum_ones', _props: { color: 'dark' } },
      { label: '2S', filter: false, sorter: true, key: 'sum_twos', _props: { color: 'dark' } },
      { label: '3S', filter: false, sorter: true, key: 'sum_threes', _props: { color: 'dark' } },
      { label: '4S', filter: false, sorter: true, key: 'sum_fours', _props: { color: 'dark' } },
      { label: '5S', filter: false, sorter: true, key: 'sum_fives', _props: { color: 'dark' } },
      { label: '6S', filter: false, sorter: true, key: 'sum_sixes', _props: { color: 'dark' } },
      { label: '7S', filter: false, sorter: true, key: 'sum_sevens', _props: { color: 'dark' } },
      { label: '8S', filter: false, sorter: true, key: 'sum_eights', _props: { color: 'dark' } },
      { label: '9S', filter: false, sorter: true, key: 'sum_nines', _props: { color: 'dark' } },
      { label: 'OUTS', filter: false, sorter: true, key: 'sum_outs', _props: { color: 'dark' } },
      { label: 'OVR', filter: false, sorter: true, key: 'sum_overs', _props: { color: 'dark' } },
      { label: 'MDN', filter: false, sorter: true, key: 'sum_mdns', _props: { color: 'dark' } },
      { label: 'WKS', filter: false, sorter: true, key: 'sum_wks', _props: { color: 'dark' } },
      { label: 'CS', filter: false, sorter: true, key: 'sum_cs', _props: { color: 'dark' } },
      { label: 'CWK', filter: false, sorter: true, key: 'sum_cwks', _props: { color: 'dark' } },
      { label: 'STS', filter: false, sorter: true, key: 'sum_sts', _props: { color: 'dark' } },
      { label: 'ROD', filter: false, sorter: true, key: 'sum_rods', _props: { color: 'dark' } },
      { label: 'ROA', filter: false, sorter: true, key: 'sum_roas', _props: { color: 'dark' } },
      { label: 'DKS', filter: false, sorter: true, key: 'sum_dks', _props: { color: 'dark' } },
      { label: 'HT', filter: false, sorter: true, key: 'sum_hattrick', _props: { color: 'dark' } },
      { label: 'DOTB', filter: false, sorter: true, key: 'sum_dotb', _props: { color: 'dark' } },
    ]
  }

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey)
    setVisitedTabs((prev) => (prev.has(tabKey) ? prev : new Set(prev).add(tabKey)))
  }

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <div className="text-medium-emphasis small mb-1">
            <span role="button" className="text-primary" onClick={() => navigate('/players')}>
              Players
            </span>{' '}
            / Performance
          </div>
          <h3 className="fw-bold mb-1">Player Performance</h3>
          <div className="text-medium-emphasis">
            Review player statistics, fantasy points, values and trends.
          </div>
        </div>
        <CButton color="secondary" variant="outline" onClick={() => navigate('/players')}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back to Players
        </CButton>
      </div>

      <ul className="nav nav-underline mb-3">
        {TABS.map((tab) => (
          <li className="nav-item" key={tab.key}>
            <button
              type="button"
              className={`nav-link${activeTab === tab.key ? ' active fw-semibold' : ''}`}
              onClick={() => handleTabChange(tab.key)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      <div style={{ display: activeTab === 'statistics' ? 'block' : 'none' }}>
        {gameDetails?.game_type === 3 ? (
          <PlayerStatTableIndoor
            users={users}
            activePage={activePage}
            setActivePage={setActivePage}
            columnFilter={columnFilter}
            setColumnFilter={setColumnFilter}
            columnSorter={columnSorter}
            setColumnSorter={setColumnSorter}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            columns={columns}
            fetchData={fetchData}
            setLoading={setLoading}
            loading={loading}
            playerList={playerList}
          />
        ) : (
          <PlayerStatTable
            users={users}
            activePage={activePage}
            setActivePage={setActivePage}
            columnFilter={columnFilter}
            setColumnFilter={setColumnFilter}
            columnSorter={columnSorter}
            setColumnSorter={setColumnSorter}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            columns={columns}
            fetchData={fetchData}
            setLoading={setLoading}
            loading={loading}
            playerList={playerList}
          />
        )}
      </div>

      {visitedTabs.has('values') && (
        <div style={{ display: activeTab === 'values' ? 'block' : 'none' }}>
          <PlayerValue />
        </div>
      )}
      {visitedTabs.has('round_points') && (
        <div style={{ display: activeTab === 'round_points' ? 'block' : 'none' }}>
          <PlayerRound />
        </div>
      )}
      {visitedTabs.has('charts') && (
        <div style={{ display: activeTab === 'charts' ? 'block' : 'none' }}>
          <PlayerChart />
        </div>
      )}
    </>
  )
}

export default PlayerStats
